/**
 * BashOutput — terminal-styled output with exit code badge
 *
 * @sync OpenCode → strips ANSI escape codes from terminal output
 * @papan-only divergence: uses the shared collapseProgressOutput, which
 *   is a SUPERSET of OpenCode's ANSI-only strip — it also collapses
 *   carriage-return progress rewrites (`git pull`/`curl`/`pip`) to their
 *   final line state instead of an unreadable concatenated run.
 */
import { useState } from 'react'
import { ChevronDown, ChevronRight } from 'lucide-react'
import { collapseProgressOutput } from '@papan/types/text'

interface Props {
  output: string
  exitCode?: number
  command?: string
  wrapLines?: boolean
}

export function BashOutput({ output, exitCode, command, wrapLines }: Props) {
  const isError = exitCode !== undefined && exitCode !== 0
  const cleanOutput = collapseProgressOutput(output)
  // The command header is its own collapsible: truncated (one line) by default,
  // click to reveal the full command wrapped, click again to collapse.
  const [cmdExpanded, setCmdExpanded] = useState(false)

  return (
    <div className="rounded-md overflow-hidden border themed-border">
      {/* Header with command — click to expand/collapse the full command */}
      {command && (
        <button
          type="button"
          onClick={() => setCmdExpanded(v => !v)}
          title={cmdExpanded ? 'Collapse command' : 'Show full command'}
          className="w-full px-3 py-1.5 themed-bg-tertiary border-b themed-border flex items-start gap-2 text-left hover:opacity-90 transition-opacity"
        >
          {cmdExpanded
            ? <ChevronDown size={12} className="themed-text-muted shrink-0 mt-0.5" />
            : <ChevronRight size={12} className="themed-text-muted shrink-0 mt-0.5" />}
          <span className={`text-xs font-mono themed-text-muted min-w-0 flex-1 ${cmdExpanded ? 'whitespace-pre-wrap break-all' : 'truncate'}`}>{command}</span>
          {exitCode !== undefined && (
            <span className={`text-[10px] px-1.5 py-0.5 rounded-full shrink-0 mt-px ${
              isError
                ? 'bg-red-500/20 text-red-400'
                : 'bg-green-500/20 text-green-400'
            }`}>
              {isError ? `exit ${exitCode}` : 'ok'}
            </span>
          )}
        </button>
      )}

      {/* Output */}
      <pre className={`p-3 text-xs font-mono themed-text max-h-[300px] overflow-y-auto bg-black/20 ${
        wrapLines ? 'whitespace-pre-wrap break-words' : 'overflow-x-auto'
      }`}>
        {cleanOutput || '(no output)'}
      </pre>
    </div>
  )
}
